// Production (Docker nginx): REACT_APP_API_URL=/api. Local `npm start`: same default + package.json
// "proxy" forwards /api/* to http://localhost:5001/api/*. Override with REACT_APP_API_URL if needed.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const getAuthToken = () => localStorage.getItem('token');

/** Read body once; avoid response.json() throwing on HTML error pages (502, CRA index.html, etc.). */
async function readJsonResponse(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    const hint = text.trimStart().startsWith('<')
      ? 'Got HTML instead of JSON — is the API URL correct and is the backend running? (Docker: docker compose ps; local: backend on port 5001.)'
      : `Response was not JSON: ${text.slice(0, 160)}`;
    throw new Error(`${hint} (HTTP ${response.status})`);
  }
}

const apiCall = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await readJsonResponse(response);
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      const msg = (data && data.error) || '';
      // Clear stale session on expired/invalid/deactivated token (not on "Admin access required")
      if (
        response.status === 401 ||
        /expired|invalid|deactivated|access token required/i.test(msg)
      ) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    throw new Error((data && data.error) || `Request failed (${response.status})`);
  }
  return data ?? {};
};

export const authAPI = {
  login: (username, password) => apiCall('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }),
  register: (userData) => apiCall('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
};

export const usersAPI = {
  getAll: () => apiCall('/admin/users'),
  create: (userData) => apiCall('/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  update: (id, userData) => apiCall(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
  delete: (id) => apiCall(`/admin/users/${id}`, {
    method: 'DELETE',
  }),
};

/** Admin: US HTS CSV import into hs_codes */
export const adminHsCodesAPI = {
  summary: () => apiCall('/admin/hs-codes/summary'),
  importCsv: async (file, { edition, effective_from } = {}) => {
    const token = getAuthToken();
    const fd = new FormData();
    fd.append('file', file);
    if (edition) fd.append('edition', edition);
    if (effective_from) fd.append('effective_from', effective_from);
    const res = await fetch(`${API_BASE_URL}/admin/hs-codes/import`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText);
    return data;
  },
};

export const driversAPI = {
  getAll: () => apiCall('/drivers'),
  create: (driverData) => apiCall('/drivers', {
    method: 'POST',
    body: JSON.stringify(driverData),
  }),
  update: (id, driverData) => apiCall(`/drivers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(driverData),
  }),
  delete: (id) => apiCall(`/drivers/${id}`, {
    method: 'DELETE',
  }),
};

export const trucksAPI = {
  getAll: () => apiCall('/trucks'),
  create: (truckData) => apiCall('/trucks', {
    method: 'POST',
    body: JSON.stringify(truckData),
  }),
  update: (id, truckData) => apiCall(`/trucks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(truckData),
  }),
  delete: (id) => apiCall(`/trucks/${id}`, {
    method: 'DELETE',
  }),
};

export const shippersAPI = {
  getAll: () => apiCall('/shippers'),
  create: (shipperData) => apiCall('/shippers', {
    method: 'POST',
    body: JSON.stringify(shipperData),
  }),
  update: (id, shipperData) => apiCall(`/shippers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(shipperData),
  }),
  delete: (id) => apiCall(`/shippers/${id}`, {
    method: 'DELETE',
  }),
};

export const carriersAPI = {
  getAll: () => apiCall('/carriers'),
  create: (carrierData) => apiCall('/carriers', {
    method: 'POST',
    body: JSON.stringify(carrierData),
  }),
  update: (id, carrierData) => apiCall(`/carriers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(carrierData),
  }),
  delete: (id) => apiCall(`/carriers/${id}`, {
    method: 'DELETE',
  }),
};

export const consigneesAPI = {
  getAll: () => apiCall('/consignees'),
  create: (consigneeData) => apiCall('/consignees', {
    method: 'POST',
    body: JSON.stringify(consigneeData),
  }),
  update: (id, consigneeData) => apiCall(`/consignees/${id}`, {
    method: 'PUT',
    body: JSON.stringify(consigneeData),
  }),
  delete: (id) => apiCall(`/consignees/${id}`, {
    method: 'DELETE',
  }),
};

export const freightAPI = {
  getAll: () => apiCall('/freight'),
  getById: (id) => apiCall(`/freight/${id}`),
  getInventoryItems: (id) => apiCall(`/freight/${id}/inventory-items`),
  create: (freightData) => apiCall('/freight', {
    method: 'POST',
    body: JSON.stringify(freightData),
  }),
  update: (id, freightData) => apiCall(`/freight/${id}`, {
    method: 'PUT',
    body: JSON.stringify(freightData),
  }),
  delete: (id) => apiCall(`/freight/${id}`, {
    method: 'DELETE',
  }),
  importPdf: async (file) => {
    const token = getAuthToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE_URL}/freight/import/pdf`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: fd,
    });
    const data = await readJsonResponse(res);
    if (!res.ok) throw new Error((data && data.error) || `Import parse failed (${res.status})`);
    return data;
  },
  confirmImport: (payload) => apiCall('/freight/import/confirm', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

export const manifestsAPI = {
  getAll: () => apiCall('/manifests'),
  create: (manifestData) => apiCall('/manifests', {
    method: 'POST',
    body: JSON.stringify(manifestData),
  }),
  update: (id, manifestData) => apiCall(`/manifests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(manifestData),
  }),
  delete: (id) => apiCall(`/manifests/${id}`, {
    method: 'DELETE',
  }),
};

export const manifestTemplatesAPI = {
  getAll: () => apiCall('/manifest-templates'),
  create: (data) => apiCall('/manifest-templates', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiCall(`/manifest-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiCall(`/manifest-templates/${id}`, {
    method: 'DELETE',
  }),
};

export const warehouseRowsAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/warehouse-rows${qs ? `?${qs}` : ''}`);
  },
  create: (rowData) => apiCall('/warehouse-rows', {
    method: 'POST',
    body: JSON.stringify(rowData),
  }),
  update: (id, rowData) => apiCall(`/warehouse-rows/${id}`, {
    method: 'PUT',
    body: JSON.stringify(rowData),
  }),
  delete: (id) => apiCall(`/warehouse-rows/${id}`, {
    method: 'DELETE',
  }),
};

export const warehouseBinsAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/warehouse-bins${qs ? `?${qs}` : ''}`);
  },
  create: (binData) => apiCall('/warehouse-bins', {
    method: 'POST',
    body: JSON.stringify(binData),
  }),
  update: (id, binData) => apiCall(`/warehouse-bins/${id}`, {
    method: 'PUT',
    body: JSON.stringify(binData),
  }),
  delete: (id) => apiCall(`/warehouse-bins/${id}`, {
    method: 'DELETE',
  }),
};

export const hsCodesAPI = {
  search: (q, limit = 50) => {
    const qs = new URLSearchParams({ q, limit: String(limit) }).toString();
    return apiCall(`/hs-codes?${qs}`);
  },
};

export const inventoryItemsAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/inventory-items${qs ? `?${qs}` : ''}`);
  },
  /** Items at or below effective low-stock threshold (see defaultLowStockThreshold in response). */
  getLowStock: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/inventory-items/low-stock${qs ? `?${qs}` : ''}`);
  },
  create: (itemData) => apiCall('/inventory-items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  update: (id, itemData) => apiCall(`/inventory-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),
  delete: (id) => apiCall(`/inventory-items/${id}`, {
    method: 'DELETE',
  }),
  transfer: (id, transferData) => apiCall(`/inventory-items/${id}/transfer`, {
    method: 'POST',
    body: JSON.stringify(transferData),
  }),
  /** Admin: download CSV (optional consignee_id matches list filter). */
  exportCsv: async (consigneeId) => {
    const token = localStorage.getItem('token');
    const qs = consigneeId ? `?${new URLSearchParams({ consignee_id: String(consigneeId) })}` : '';
    const res = await fetch(`${API_BASE_URL}/admin/inventory-items/export${qs}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!res.ok) {
      const t = await res.text();
      let msg = res.statusText;
      try {
        const data = JSON.parse(t);
        if (data.error) msg = data.error;
      } catch {
        if (t) msg = t.slice(0, 200);
      }
      throw new Error(msg);
    }
    return res.blob();
  },
  /** Admin: multipart CSV import; returns { inserted, updated, skipped, errors }. */
  importCsv: async (file) => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE_URL}/admin/inventory-items/import`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText);
    return data;
  },
};

export const inventoryTransfersAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/inventory-transfers${qs ? `?${qs}` : ''}`);
  },
};

export const routingAPI = {
  listDepots: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/routing/depots${qs ? `?${qs}` : ''}`);
  },
  createDepot: (data) =>
    apiCall('/routing/depots', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateDepot: (id, data) =>
    apiCall(`/routing/depots/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteDepot: (id) =>
    apiCall(`/routing/depots/${id}`, { method: 'DELETE' }),
  listPlans: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/routing/plans${qs ? `?${qs}` : ''}`);
  },
  getPlan: (id) => apiCall(`/routing/plans/${id}`),
  createPlan: (data) => apiCall('/routing/plans', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updatePlan: (id, data) => apiCall(`/routing/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deletePlan: (id) => apiCall(`/routing/plans/${id}`, { method: 'DELETE' }),
  bulkAddManifests: (planId, manifest_ids) =>
    apiCall(`/routing/plans/${planId}/stops/bulk`, {
      method: 'POST',
      body: JSON.stringify({ manifest_ids }),
    }),
  reorderStops: (planId, stop_ids) =>
    apiCall(`/routing/plans/${planId}/stops/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ stop_ids }),
    }),
  removeStop: (planId, stopId) =>
    apiCall(`/routing/plans/${planId}/stops/${stopId}`, { method: 'DELETE' }),
  optimize: (planId, options = {}) =>
    apiCall(`/routing/plans/${planId}/optimize`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),
  getJob: (jobId) => apiCall(`/routing/jobs/${jobId}`),
  publish: (planId) =>
    apiCall(`/routing/plans/${planId}/publish`, { method: 'POST' }),
};

export const opsAPI = {
  getMe: () => apiCall('/ops/me'),
  getSettings: () => apiCall('/ops/settings'),
  updateSettings: (data) =>
    apiCall('/ops/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getAssignment: () => apiCall('/ops/me/assignment'),
  getTrackingLatest: () => apiCall('/ops/tracking/latest'),
  getStopEvents: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/ops/stop-events${qs ? `?${qs}` : ''}`);
  },
  getExceptionReasons: () => apiCall('/ops/exception-reasons'),
  getExceptions: () => apiCall('/ops/exceptions'),
  getBreakdowns: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/ops/breakdowns${qs ? `?${qs}` : ''}`);
  },
  resolveBreakdown: (id) =>
    apiCall(`/ops/breakdowns/${id}/resolve`, { method: 'PUT' }),
  getDocuments: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/ops/documents${qs ? `?${qs}` : ''}`);
  },
  getDocument: (id) => apiCall(`/ops/documents/${id}`),
  emailDocument: (id, to) =>
    apiCall(`/ops/documents/${id}/email`, {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),
  getRate: (freightId) => apiCall(`/ops/loads/${freightId}/rate`),
  saveRate: (freightId, data) =>
    apiCall(`/ops/loads/${freightId}/rate`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getInvoices: () => apiCall('/ops/invoices'),
  createInvoice: (data) =>
    apiCall('/ops/invoices', { method: 'POST', body: JSON.stringify(data) }),
  getReports: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/ops/reports${qs ? `?${qs}` : ''}`);
  },
};

